import isEqual from 'lodash/isEqual'

import { Topic } from 'common/components/topics/interfaces'

import { FormSubmitAction, PageState } from './BriefPage'

export const getSubmitAction = (
  formValues: any,
  generateBrief: FormSubmitAction,
  generateVariants: FormSubmitAction
): FormSubmitAction => {
  const submitAction = formValues?.submitAction

  switch (submitAction) {
    case 'formChangedWithTopics':
    case 'formChangedWithBrief':
    case 'generateBrief':
    case 'refreshBrief':
      return generateBrief
    case 'generateWithoutTopics':
    case 'formChangedWithoutTopics':
    case 'topicsChanged':
    case 'generateVariants':
    case 'brandVoiceChanged':
    case 'formChangedWithBriefFocusThemeOnly':
      return generateVariants
    default:
      return async () => {}
  }
}

type BriefPageStateInput = {
  hasFormChanged: boolean
  hasBrandVoiceChanged: boolean
  hasMergeTagsChanged: boolean
  shouldUseTopics: boolean | undefined
  generatedBrief: string | undefined
  focusThemeOnly: boolean
  showContentImport: boolean
  hasVariants: boolean | undefined
  topics: Topic[] | undefined
  selectedTopics: Topic[]
}

export const getBriefPageState = ({
  hasFormChanged,
  hasBrandVoiceChanged,
  hasMergeTagsChanged,
  shouldUseTopics,
  generatedBrief,
  focusThemeOnly,
  showContentImport,
  hasVariants,
  topics,
  selectedTopics,
}: BriefPageStateInput): PageState => {
  const hasTopics = topics !== undefined
  // Treat missing topics the same as [] so adding a Focus Theme on a
  // duplicated (or never-generated-with-themes) element still counts as dirty.
  // In focus-theme-only mode, selectedTopics is constants-only — compare
  // against the same baseline or NER leftovers permanently look "changed".
  const persistedTopics = focusThemeOnly
    ? (topics?.filter((topic) => topic.isConstant) ?? [])
    : (topics ?? [])
  const hasTopicsChanged = !isEqual(persistedTopics, selectedTopics)

  if (hasBrandVoiceChanged && !hasFormChanged && hasVariants) {
    return 'brandVoiceChanged'
  } else if (hasMergeTagsChanged && !hasFormChanged && hasVariants) {
    return 'mergeTagsChanged'
  } else if (hasFormChanged && !shouldUseTopics && hasVariants) {
    return 'formChangedWithoutTopics'
  } else if (
    (hasFormChanged || hasTopicsChanged) &&
    shouldUseTopics &&
    generatedBrief &&
    focusThemeOnly
  ) {
    return 'formChangedWithBriefFocusThemeOnly'
  } else if (hasFormChanged && shouldUseTopics && hasTopics) {
    return 'formChangedWithTopics'
  } else if (hasFormChanged && shouldUseTopics && generatedBrief) {
    return 'formChangedWithBrief'
  } else if (hasTopicsChanged && hasVariants) {
    return 'topicsChanged'
  } else if (!shouldUseTopics && hasVariants) {
    return 'showGeneratedContentWithoutTopics'
  } else if (!shouldUseTopics && !hasVariants && !hasTopics) {
    return 'generateWithoutTopics'
  } else if (
    !hasVariants &&
    (generatedBrief || showContentImport) &&
    (hasTopics || focusThemeOnly)
  ) {
    return 'generateVariants'
  } else if (hasVariants) {
    return 'showGeneratedContent'
  } else {
    return 'generateBrief'
  }
}

const APPLY_BRIEF_TO_ALL_TEMPLATES_RULES: Record<
  'focusThemeOnly' | 'default',
  { states: readonly PageState[]; requiresUniversalBriefSelected: boolean }
> = {
  focusThemeOnly: {
    states: [
      'generateBrief',
      'generateVariants',
      'formChangedWithBriefFocusThemeOnly',
    ],
    // Unticking moves the selection to the first template, so requiring the
    // universal brief here would unmount the only control that can re-tick it.
    requiresUniversalBriefSelected: false,
  },
  default: {
    states: ['generateBrief'],
    requiresUniversalBriefSelected: true,
  },
}

type ApplyBriefToAllTemplatesInput = {
  pageState: PageState
  isBriefStepActive: boolean
  isUniversalBriefSelected: boolean
  focusThemeOnly: boolean
  showBriefingV2: boolean
}

export const shouldShowApplyBriefToAllTemplates = ({
  pageState,
  isBriefStepActive,
  isUniversalBriefSelected,
  focusThemeOnly,
  showBriefingV2,
}: ApplyBriefToAllTemplatesInput): boolean => {
  if (!isBriefStepActive) {
    return false
  }

  if (!focusThemeOnly && !showBriefingV2) {
    return false
  }

  const rule =
    APPLY_BRIEF_TO_ALL_TEMPLATES_RULES[
      focusThemeOnly ? 'focusThemeOnly' : 'default'
    ]

  if (rule.requiresUniversalBriefSelected && !isUniversalBriefSelected) {
    return false
  }

  return rule.states.includes(pageState)
}
